import { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { CategorySelector, CRITERIA_CATEGORY_COLORS, type CriteriaCategory } from "@/components/CategorySelector";
import { fieldLabelClass, textareaClass } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { useUpdateTeacherComment } from "@/api/hooks";
import { useNotification } from "@/providers/NotificationProvider";
import type { NotificationKind } from "@/providers/NotificationProvider";
import type { TeacherComment } from "@markeroo/shared";
import { notifications, ui } from "@/strings";

const MAX_COMMENT_LENGTH = 1000;

/**
 * Infer category from a hex color.
 * Used for backward compatibility with existing comments that only have color.
 * Returns 'other' if the color doesn't match any category.
 */
function inferCategoryFromColor(hexColor: string): CriteriaCategory {
  const normalizedColor = hexColor.toLowerCase();
  for (const [category, color] of Object.entries(CRITERIA_CATEGORY_COLORS)) {
    if (color.toLowerCase() === normalizedColor) {
      return category as CriteriaCategory;
    }
  }
  // Default to 'other' for colors that don't match any category
  return "other";
}

interface EditCommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignmentId: string;
  documentId: string;
  comment: TeacherComment;
}

export function EditCommentModal({
  isOpen,
  onClose,
  assignmentId,
  documentId,
  comment,
}: EditCommentModalProps) {
  // Use existing category if present, otherwise infer from color for backward compat
  const initialCategory = (comment.category as CriteriaCategory | undefined) ?? inferCategoryFromColor(comment.color);

  const [commentText, setCommentText] = useState(comment.comment_text);
  const [category, setCategory] = useState<CriteriaCategory>(initialCategory);
  const { show } = useNotification();
  const TOAST_SUCCESS: NotificationKind = "success";
  const TOAST_ERROR: NotificationKind = "error";

  // Derive color from category
  const color = CRITERIA_CATEGORY_COLORS[category];

  const updateCommentMutation = useUpdateTeacherComment(assignmentId, documentId);

  // Sync with prop changes
  useEffect(() => {
    if (isOpen) {
      setCommentText(comment.comment_text);
      const newCategory = (comment.category as CriteriaCategory | undefined) ?? inferCategoryFromColor(comment.color);
      setCategory(newCategory);
    }
  }, [isOpen, comment]);

  const handleSubmit = async () => {
    if (!commentText.trim()) {
      show(notifications.comments.enterCommentRequired, TOAST_ERROR);
      return;
    }

    if (commentText.length > MAX_COMMENT_LENGTH) {
      show(notifications.comments.maxLengthExceeded(MAX_COMMENT_LENGTH), TOAST_ERROR);
      return;
    }

    try {
      const originalCategory = (comment.category as CriteriaCategory | undefined) ?? inferCategoryFromColor(comment.color);
      await updateCommentMutation.mutateAsync({
        commentId: comment.id,
        payload: {
          comment_text: commentText.trim(),
          category: category !== originalCategory ? category : undefined,
        },
      });

      show(notifications.comments.updated, TOAST_SUCCESS);
      onClose();
    } catch {
      show(notifications.comments.updateFailed, TOAST_ERROR);
    }
  };

  const isOverLimit = commentText.length > MAX_COMMENT_LENGTH;
  const charCount = commentText.length;
  const originalCategory = (comment.category as CriteriaCategory | undefined) ?? inferCategoryFromColor(comment.color);
  const hasChanges = commentText !== comment.comment_text || category !== originalCategory;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader title={`${ui.actions.edit} ${ui.labels.comment}`} onClose={onClose} />
      <ModalBody>
        {/* Preview Section */}
        <div className="rounded-lg border border-border bg-surface-secondary p-4">
          <div className="text-sm text-text-theme-secondary space-y-2">
            {comment.before_context && (
              <p className="text-text-theme-muted">{comment.before_context}</p>
            )}
            <p>
              <span
                className="rounded px-1"
                style={{ backgroundColor: `${color}33`, borderBottom: `2px solid ${color}` }}
              >
                {comment.phrase}
              </span>
            </p>
            {comment.after_context && (
              <p className="text-text-theme-muted">{comment.after_context}</p>
            )}
          </div>

          {/* Preview Tooltip */}
          {commentText.trim() && (
            <div className="mt-4 rounded-xl border border-border bg-white p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <span
                  className="h-2 w-10 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: color }}
                />
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-semibold text-text-theme-secondary">
                    {comment.teacher_name} {ui.labels.says}
                  </p>
                  <p className="text-sm text-text-theme-secondary">{commentText}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Category Picker */}
        <CategorySelector selectedCategory={category} onCategoryChange={setCategory} />

        {/* Comment Input */}
        <label className={fieldLabelClass}>
          {ui.labels.comment}
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            className={textareaClass}
            rows={4}
            autoFocus
          />
          <div className="flex justify-between text-xs mt-1">
            <span className="text-text-theme-muted">
              {ui.labels.visibleToStudent}
            </span>
            <span className={isOverLimit ? "text-semantic-error" : "text-text-theme-muted"}>
              {charCount} {" "}
              {"/"}
              {" "}
              {MAX_COMMENT_LENGTH}
            </span>
          </div>
        </label>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          onClick={onClose}
          className={buttonVariants.secondary}
          disabled={updateCommentMutation.isPending}
        >
          {ui.actions.cancel}
        </button>
        <button
          type="button"
          onClick={() => void handleSubmit()}
          className={buttonVariants.primary}
          disabled={
            !commentText.trim() ||
            isOverLimit ||
            !hasChanges ||
            updateCommentMutation.isPending
          }
        >
          {updateCommentMutation.isPending ? ui.actions.saving : ui.actions.save}
        </button>
      </ModalFooter>
    </Modal>
  );
}
